// Kunden-Version (08.10.2026): Einstieg ohne Firmenlink auf scan.umzugsscan.app.
// E-Mail eingeben → 6-stelliger Code (oder Link aus der Mail) → eigene Aufnahme.
// Kein Passwort; mit derselben E-Mail kommt man später wieder hinein.

import { useState } from "react";
import { PRIVATE_CAPTURE_CENTS } from "@umzugsscan/shared";

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
import { api } from "../api.ts";
import { errorText } from "../texte.ts";
import { Button, Field } from "../ui/elemente.tsx";
import { AufsHandy } from "../ui/AufsHandy.tsx";
import { RechtLinks } from "../ui/Recht.tsx";

export function PrivatKopf() {
  return (
    <header className="kopf">
      <div className="kopf-row">
        <img className="logo logo-marke" src="/umzugsscan-logo.svg" alt="Umzugsscan" />
      </div>
    </header>
  );
}

export function PrivatStart(props: { notice?: string | null; onToken: (token: string) => void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(props.notice ?? null);

  const requestCode = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Bitte eine gültige E-Mail-Adresse eingeben.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.request("POST", "/privat/anmelden", { email: email.trim() });
      setSent(true);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.request<{ token: string }>("POST", "/privat/bestaetigen", { email: email.trim(), code: code.trim() });
      props.onToken(r.token);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  return (
    <div className="app">
      <PrivatKopf />
      <main className="main">
        {!sent ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void requestCode();
            }}
          >
            <p className="eyebrow">Für alle, die umziehen</p>
            <h1>Umzugsangebote ohne Besichtigung</h1>
            {/* 12.10.2026: Der Kern ist die Auswertung durch die KI und dass keine Besichtigung nötig ist, nicht das PDF. */}
            <p className="lead">
              Sie fotografieren Ihre Wohnung mit dem Handy. Unsere künstliche Intelligenz erkennt auf den Fotos Möbel, Geräte
              und Kartons und ermittelt daraus mit Ihren Angaben das Umzugsvolumen in m³ und den Umfang des Auftrags.
            </p>
            <p>
              So muss keine Umzugsfirma zur Besichtigung kommen: Sie schicken bequem von zu Hause eine Anfrage, mit der Firmen
              belastbar kalkulieren können – an so viele Firmen, wie Sie möchten.
            </p>
            <ol className="steps intro-steps">
              <li>E-Mail-Adresse bestätigen (kein Passwort)</li>
              <li>Adressen, Termin und Fotos Raum für Raum – etwa 15 Minuten</li>
              <li>Für {euro(PRIVATE_CAPTURE_CENTS)} von der KI auswerten lassen: Volumen in m³, Möbelliste, Kartons und Fotos als PDF</li>
              <li>PDF an Umzugsfirmen schicken und Angebote vergleichen</li>
            </ol>
            <AufsHandy />
            <section className="card">
              <Field label="E-Mail-Adresse" type="email" inputMode="email" autoComplete="email" value={email} onChange={setEmail} />
              {error && <p className="error">{error}</p>}
              <Button type="submit" disabled={busy}>
                {busy ? "Einen Moment …" : "Code per E-Mail anfordern"}
              </Button>
              <p className="hint">
                Schon angefangen? Einfach dieselbe E-Mail-Adresse eingeben, dann geht es dort weiter.
              </p>
            </section>
            <p className="hint">
              Sie sind ein Umzugsunternehmen? <a href="https://umzugsscan.app">Umzugsscan für Firmen</a>
            </p>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void confirm();
            }}
          >
            <h1>Code eingeben</h1>
            <p className="lead">
              Wir haben einen 6-stelligen Code an <strong>{email.trim()}</strong> geschickt. Sie können auch einfach den Link
              in der E-Mail öffnen.
            </p>
            <section className="card">
              <Field
                label="Code aus der E-Mail"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(v) => setCode(v.replace(/\D/g, ""))}
              />
              {error && <p className="error">{error}</p>}
              <Button type="submit" disabled={busy || code.length !== 6}>
                {busy ? "Einen Moment …" : "Bestätigen"}
              </Button>
            </section>
            <p className="hint">Keine E-Mail? Bitte auch im Spam-Ordner nachsehen. Der Code gilt 30 Minuten.</p>
            <div className="stack">
              <Button variant="secondary" onClick={() => void requestCode()} disabled={busy}>
                Neuen Code senden
              </Button>
              <Button
                variant="quiet"
                onClick={() => {
                  setSent(false);
                  setCode("");
                  setError(null);
                }}
              >
                Andere E-Mail-Adresse
              </Button>
            </div>
          </form>
        )}
        <RechtLinks privat />
      </main>
    </div>
  );
}
